import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { setTokens } from "@/shared/infrastructure/auth/token-storage";
import type {
  AcceptTermsResult,
  PendingTerm,
  TermsAcceptance,
  TermsAcceptanceItem,
  TermsDocument,
} from "../../domain/entities";
import { getCurrentTermsUseCase } from "../../application/use-cases/get-current-terms.use-case";
import { getPendingTermsUseCase } from "../../application/use-cases/get-pending-terms.use-case";
import { acceptTermsUseCase } from "../../application/use-cases/accept-terms.use-case";
import { getAcceptanceHistoryUseCase } from "../../application/use-cases/get-acceptance-history.use-case";
import { termsHttpRepository } from "../http/terms.http-repository";
import { subscribeToTermsRequired } from "../terms-interceptor";

export const TERMS_QUERY_KEYS = {
  all: ["terms"] as const,
  current: ["terms", "current"] as const,
  pending: ["terms", "pending"] as const,
  history: ["terms", "history"] as const,
};

export function useCurrentTermsQuery() {
  return useQuery<TermsDocument[]>({
    queryKey: TERMS_QUERY_KEYS.current,
    queryFn: () => getCurrentTermsUseCase(termsHttpRepository),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePendingTermsQuery(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = subscribeToTermsRequired((pending) => {
      if (pending !== undefined) {
        queryClient.setQueryData(TERMS_QUERY_KEYS.pending, pending);
      } else {
        queryClient.invalidateQueries({ queryKey: TERMS_QUERY_KEYS.pending });
      }
    });
    return unsubscribe;
  }, [queryClient]);

  return useQuery<PendingTerm[]>({
    queryKey: TERMS_QUERY_KEYS.pending,
    queryFn: () => getPendingTermsUseCase(termsHttpRepository),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

export function useAcceptTermsMutation() {
  const queryClient = useQueryClient();

  return useMutation<AcceptTermsResult, Error, TermsAcceptanceItem[]>({
    mutationFn: (items: TermsAcceptanceItem[]) =>
      acceptTermsUseCase(termsHttpRepository, items),
    onSuccess: (result) => {
      setTokens(result);
      queryClient.setQueryData(TERMS_QUERY_KEYS.pending, result.pendingTerms ?? []);
      queryClient.invalidateQueries({ queryKey: TERMS_QUERY_KEYS.all });
      queryClient.invalidateQueries();
    },
  });
}

export function useTermsHistoryQuery(options?: { enabled?: boolean }) {
  return useQuery<TermsAcceptance[]>({
    queryKey: TERMS_QUERY_KEYS.history,
    queryFn: () => getAcceptanceHistoryUseCase(termsHttpRepository),
    enabled: options?.enabled ?? true,
  });
}
